"use client"

/**
 * Leo's word on a question's classification, as one card: content categories
 * that fit, standards that fit, broader categories to add too, and a required
 * standard with nothing tagged. `useClassificationLeoCards` gives them as cards
 * for Properties' one Leo spotted stack; `ClassificationLeoSuggestions` shows
 * them as their own card at the top of the category navigation. Never inside a
 * field. With nothing to say there are no cards.
 */

import { LeoCategorySuggestions, useCategorySuggestionCards } from "@/components/question-composer/leo-category-suggestions"
import { useRequiredStandardCards } from "@/components/question-composer/standards-field"
import { useQuestionDimensions, useTaggableStandards } from "@/hooks/use-category-dimensions"
import type { CategoryDimension } from "@/lib/exam-categories"

type ClassificationLeoInput = {
  /** Every tag on the question. */
  value: string[]
  /** A suggestion the author approved (tagged as Leo's). */
  onAdd: (categoryId: string) => void
  onAddMany: (categoryIds: string[]) => void
  /** Course content the question's course teaches. Standards are not limited by course. */
  scopeIds?: readonly string[]
  courseTrees?: CategoryDimension[]
  /** Where a missing required standard is chosen. */
  onOpenStandards: () => void
}

/** What the trees, scope and extra cards are, for either way of showing them. */
function useClassificationInput({ value, onAdd, onAddMany, scopeIds, courseTrees = [], onOpenStandards }: ClassificationLeoInput) {
  const content = useQuestionDimensions()[0]
  const standards = useTaggableStandards()
  const required = useRequiredStandardCards(value, courseTrees)
  return {
    dimensionIds: [...(content ? [content.id] : []), ...standards.map((dimension) => dimension.id)],
    value,
    scopeIds,
    scopeDimensionId: content?.id,
    onAdd,
    onAddMany,
    extraCards: required,
    onExtraPrimary: onOpenStandards,
  }
}

/** The cards and their handlers, for a stack that holds other findings too. */
export function useClassificationLeoCards(input: ClassificationLeoInput) {
  return useCategorySuggestionCards(useClassificationInput(input))
}

export function ClassificationLeoSuggestions(props: ClassificationLeoInput) {
  const input = useClassificationInput(props)
  if (input.dimensionIds.length === 0) return null
  return <LeoCategorySuggestions {...input} />
}
